/**
 * Loading Gate Service
 *
 * Replaces arbitrary setTimeout calls with event-driven loading gates.
 * Gates are registered by components and resolved when loading completes.
 *
 * Example:
 * - TaskEditPopup registers 'comments-loaded' gate when isLoadingComments changes
 * - CommentsList waits for 'comments-loaded' gate before highlighting
 */

import { logger } from '@/utils/logger';

interface GateState {
  isLoading: boolean;
  resolvers: Array<() => void>;
  timeout?: NodeJS.Timeout;
}

class LoadingGateService {
  private gates: Map<string, GateState> = new Map();
  private readonly DEFAULT_TIMEOUT_MS = 5000;

  /**
   * Register or update a loading gate
   * @param gateId - Unique identifier for the gate
   * @param isLoading - Current loading state
   */
  public register(gateId: string, isLoading: boolean): void {
    const existingGate = this.gates.get(gateId);

    if (!existingGate) {
      // Create new gate
      this.gates.set(gateId, {
        isLoading,
        resolvers: [],
      });
      return;
    }

    // Update existing gate
    existingGate.isLoading = isLoading;

    // If loading finished, resolve all waiting promises
    if (!isLoading && existingGate.resolvers.length > 0) {
      existingGate.resolvers.forEach(resolve => resolve());
      existingGate.resolvers = [];

      // Clear timeout if exists
      if (existingGate.timeout) {
        clearTimeout(existingGate.timeout);
        existingGate.timeout = undefined;
      }
    }
  }

  /**
   * Wait for a loading gate to open (isLoading becomes false)
   * @param gateId - The gate to wait for
   * @param timeoutMs - Optional timeout in milliseconds
   * @returns Promise that resolves when gate opens or times out
   */
  public wait(gateId: string, timeoutMs?: number): Promise<void> {
    const gate = this.gates.get(gateId);

    // If gate doesn't exist or is already open, resolve immediately
    if (!gate || !gate.isLoading) {
      return Promise.resolve();
    }

    // Create promise that resolves when gate opens
    return new Promise<void>(resolve => {
      gate.resolvers.push(resolve);

      // Set timeout failsafe
      const timeout = timeoutMs ?? this.DEFAULT_TIMEOUT_MS;
      gate.timeout = setTimeout(() => {
        logger.warn(
          `[LoadingGateService] Gate "${gateId}" did not open within ${timeout}ms. Resolving anyway.`
        );

        // Remove this resolver from the list
        const index = gate.resolvers.indexOf(resolve);
        if (index !== -1) {
          gate.resolvers.splice(index, 1);
        }

        resolve();
      }, timeout);
    });
  }

  /**
   * Check if a gate is currently loading
   * @param gateId - The gate to check
   * @returns true if gate exists and is loading, false otherwise
   */
  public isLoading(gateId: string): boolean {
    const gate = this.gates.get(gateId);
    return gate?.isLoading ?? false;
  }

  /**
   * Clear a gate (remove from registry)
   * @param gateId - The gate to clear
   */
  public clear(gateId: string): void {
    const gate = this.gates.get(gateId);
    if (gate?.timeout) {
      clearTimeout(gate.timeout);
    }
    this.gates.delete(gateId);
  }

  /**
   * Clear all gates (useful for cleanup/testing)
   */
  public clearAll(): void {
    this.gates.forEach(gate => {
      if (gate.timeout) {
        clearTimeout(gate.timeout);
      }
    });
    this.gates.clear();
  }
}

// Singleton instance
export const loadingGateService = new LoadingGateService();
